import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import type { DataTableCellIdCoordinate, DataTableCellReference, FlashColor } from '../DataTable.model';
import { toCellRef } from '../DataTable.util';
import { useDataTable } from '../DataTableProvider';
import { isApiId } from '@/utils/ApiId.utils';
import { distinctUntilChanged, map } from 'rxjs';

/**
 * Subscribes to the DataTableService flashCell$ stream filtered by this cell's
 * API ID coordinate. The stream retains flash state for 1s after dispatch, so
 * cells remounting mid-animation (e.g. from virtualizer scrolling) still pick
 * up their flash.
 */
export const useCellFlash = (idCoordinate: DataTableCellIdCoordinate | undefined): string => {
  const { dataTableService } = useDataTable();
  const snapshotRef = useRef<FlashColor | null>(null);

  const key: DataTableCellReference | undefined = useMemo(
    () =>
      isApiId('dtc', idCoordinate?.column) && isApiId('dtr', idCoordinate?.row) ? toCellRef(idCoordinate) : undefined,
    [idCoordinate?.column, idCoordinate?.row]
  );

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      snapshotRef.current = null;
      if (key === undefined) {
        return () => {};
      }
      const subscription = dataTableService.flashCell$
        .pipe(
          map((flashingCells) => flashingCells.get(key) ?? null),
          distinctUntilChanged()
        )
        .subscribe((color) => {
          snapshotRef.current = color;
          onStoreChange();
        });
      return () => {
        subscription.unsubscribe();
      };
    },
    [dataTableService, key]
  );

  const getSnapshot = useCallback(() => snapshotRef.current, []);

  const flashColor = useSyncExternalStore(subscribe, getSnapshot);

  return flashColor === null ? '' : `dt-cell-flash-${flashColor}`;
};
